import { uploadAttachment } from 'api/file'
import { AxiosResponse } from 'axios'
import { receiveAttachments } from 'modules/conversations/actions'
import { pathOr } from 'ramda'
import { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ATTACHMENT_SIZE_LIMIT, ATTACHMENT_STATES } from 'types/attachments'
import { ConversationId } from 'types/conversation'

interface AttachmentUploadProps {
  onAttachmentUploadComplete: Function,
  activeConversation: ConversationId,
}

const useAttachmentUpload = ({
  onAttachmentUploadComplete,
  activeConversation
}: AttachmentUploadProps) => {
  const dispatch = useDispatch()
  const [attachmentState, setAttachmentState] = useState(ATTACHMENT_STATES.NONE)
  const { allowEndUserFileUpload } = useSelector(state =>
    pathOr(false, ['embed', 'configuration', 'theme'], state)
  )

  const uploadAttachments = useCallback(async (files) => {
    if(!allowEndUserFileUpload) {return}
    if(attachmentState === ATTACHMENT_STATES.ERROR) {return}

    setAttachmentState(ATTACHMENT_STATES.PENDING)
    const uploadPromises = files
      .filter(file => file.size < ATTACHMENT_SIZE_LIMIT.DEFAULT)
      .map(file => {
        return uploadAttachment(file, activeConversation)
      })

    try {
      const responses: AxiosResponse<any>[] = await Promise.all(uploadPromises)
      const attachments = responses.map(response => response.data)
      const attachmentIds = attachments.map(attachment => attachment.id)

      dispatch(receiveAttachments({ attachments }))
      onAttachmentUploadComplete(attachmentIds)
      setAttachmentState(ATTACHMENT_STATES.NONE)
    } catch (err) {
      setAttachmentState(ATTACHMENT_STATES.ERROR)
    }
  }, [activeConversation, allowEndUserFileUpload, attachmentState, dispatch, onAttachmentUploadComplete])


  const resetAttachmentState = () => {
    setAttachmentState(ATTACHMENT_STATES.NONE)
  }

  return { attachmentState, uploadAttachments, resetAttachmentState }
}

export default useAttachmentUpload
